import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Master monogram — served from the backend as a transparent SVG. The
// canonical site accent gold `#C8A951` (softer champagne) is used across
// both nav states so the mark sits inside the brand palette without
// shouting. A drop-shadow keeps it legible over the hero's lighter
// mid-tones without needing a second color.
const LOGO_HEADER = `${BACKEND_URL}/api/monogram/gold-champagne.svg`;

const NAV_LINKS = [
  { label: 'Our Story', to: '/our-story', id: 'our-story' },
  { label: 'Gallery', to: '/gallery', id: 'gallery' },
  { label: 'Events', to: '/events', id: 'events' },
  { label: 'Schedule', to: '/schedule', id: 'schedule' },
  { label: 'Menu', to: '/menu', id: 'menu' },
  { label: 'Travel', to: '/travel', id: 'travel' },
  { label: 'Guest Book', to: '/guest-book', id: 'guest-book' },
  { label: 'FAQ', to: '/faq', id: 'faq' }
];

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // On the homepage the nav floats transparently over the hero.
  // On every other page it should always look "solid" so it never disappears
  // against page content.
  const isHome = location.pathname === '/';
  const overlay = isHome && !isScrolled;

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobileMenuOpen]);

  const goToRSVP = () => {
    setIsMobileMenuOpen(false);
    navigate('/rsvp');
  };

  const linkColor = overlay ? 'text-white/90' : 'text-[#3B2F2F]';
  const logoColor = overlay ? 'text-white' : 'text-[#3B2F2F]';
  const iconColor = overlay ? 'text-white' : 'text-[#3B2F2F]';
  const isActive = (to) => location.pathname === to;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          overlay
            ? 'bg-transparent py-4 sm:py-5'
            : 'bg-[#F8F5EF]/95 backdrop-blur-md shadow-sm py-3'
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo — always links back to the hero home. Uses the master
                M&A monogram (SVG), switching between the white overlay
                variant on the dark hero and the gold-on-ivory variant when
                the nav bar becomes opaque. Kept intentionally small so the
                mark reads as a refined brand cue, not a headline. */}
            <Link
              to="/"
              className={`inline-flex items-center transition-transform hover:scale-[1.03] ${
                overlay ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]' : ''
              }`}
              aria-label="Mary and Arrington — home"
              data-testid="nav-logo"
            >
              <img
                src={LOGO_HEADER}
                alt="Mary & Arrington monogram"
                className="h-[18px] sm:h-[20px] md:h-[22px] w-auto select-none"
                draggable="false"
              />
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-7">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.id}
                  to={link.to}
                  className={`text-[0.78rem] tracking-[0.18em] uppercase transition-colors hover:text-[#C8A951] ${
                    isActive(link.to) ? 'text-[#C8A951]' : linkColor
                  } ${overlay ? 'drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]' : ''}`}
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={goToRSVP}
                className="btn-gold text-xs py-2 px-4"
                data-testid="nav-rsvp-btn"
              >
                RSVP
              </button>
            </div>

            {/* Mobile hamburger — floats directly over the hero on the homepage */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors ${iconColor} ${
                overlay
                  ? 'bg-black/30 border border-white/25 backdrop-blur-md shadow-[0_4px_18px_rgba(0,0,0,0.25)]'
                  : 'bg-transparent'
              }`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-[#F8F5EF] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8 pt-24">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={link.to}
                    className="font-serif text-3xl text-[#3B2F2F] hover:text-[#C8A951] transition-colors"
                    data-testid={`mobile-nav-${link.id}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.button
                type="button"
                onClick={goToRSVP}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 + NAV_LINKS.length * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="btn-gold mt-4"
                data-testid="mobile-nav-rsvp-btn"
              >
                RSVP Now
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { Navigation };
